import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from "../Auth/AuthContext";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Building,
  Phone,
  FileText,
  ShieldCheck,
  X
} from "lucide-react";
import { baseUrl } from '../../config/appsettings';

const SimpleDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(false);

    const Auth = useAuth();
    const user = Auth.authState.user;

    const toggleDropdown = () => setIsOpen((prev) => !prev);

    const handleOptionClick = async (option) => {
        if (option === 'Logout') {
            localStorage.removeItem("token");
            window.location.href = "/";
        } else if (option === 'Profile') {
            await fetchUserProfile();
        }
    };

    const fetchUserProfile = async () => {
        setLoading(true);
        try {
            const userId = user?.id;
            const response = await fetch(`${baseUrl}Auth/GetUserProfile/${userId}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });

            if (!response.ok) throw new Error("Failed to fetch profile");

            const data = await response.json();
            setUserData(data);
            setIsModalOpen(true); // Open modal
        } catch (error) {
            console.error("Error fetching profile:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative inline-block text-left">
            {/* User Dropdown Button */}
            <button
                onClick={toggleDropdown}
                className="inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none"
            >
                <FontAwesomeIcon icon={faUser} className="text-gray-700 hover:text-gray-300" />
                <svg
                    className="-mr-1 ml-2 h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute right-0 z-10 mt-2 w-40 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                    <div className="py-1">
                        <button
                            onClick={() => handleOptionClick('Profile')}
                            className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                            {loading ? "Loading..." : "Profile"}
                        </button>
                        <button
                            onClick={() => handleOptionClick('Logout')}
                            className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                            Logout
                        </button>
                    </div>
                </div>
            )}

            {isModalOpen && userData && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm"
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 relative border border-gray-200"
                    >
                        {/* Close Button */}
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-red-500 transition"
                        >
                            <X size={24} />
                        </button>

                        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                            <User className="inline-block mr-2 text-blue-600" />
                            User Profile
                        </h2>

                        <div className="space-y-5 text-gray-700">
                            <div className="flex items-center gap-3">
                                <User className="text-blue-500" />
                                <div>
                                    <label className="text-sm text-gray-500">Name</label>
                                    <p className="font-medium">{userData.name}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <Mail className="text-blue-500" />
                                <div>
                                    <label className="text-sm text-gray-500">Email</label>
                                    <p className="font-medium">{userData.email}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <Building className="text-blue-500" />
                                <div>
                                    <label className="text-sm text-gray-500">Company</label>
                                    <p className="font-medium">{userData.company}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <Phone className="text-blue-500" />
                                <div>
                                    <label className="text-sm text-gray-500">Phone</label>
                                    <p className="font-medium">{userData.phone}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <FileText className="text-blue-500" />
                                <div>
                                    <label className="text-sm text-gray-500">Invoice Enabled</label>
                                    <p
                                        className={`font-semibold ${userData.isInvoiceEnable ? "text-green-600" : "text-red-500"
                                            }`}
                                    >
                                        {userData.isInvoiceEnable ? "Yes" : "No"}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <ShieldCheck className="text-blue-500" />
                                <div>
                                    <label className="text-sm text-gray-500">Approved By Admin</label>
                                    <p
                                        className={`font-semibold ${userData.isApprovedByAdmin ? "text-green-600" : "text-red-500"
                                            }`}
                                    >
                                        {userData.isApprovedByAdmin ? "Yes" : "No"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Bottom Close Button */}
                        <div className="mt-6 flex justify-center">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200"
                            >
                                Close
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
};

export default SimpleDropdown;
